import styles from './NewAgentTaskForm.module.css'
import {clsx} from 'clsx'
import {
  NewTaskChatInput,
  type NewTaskChatInputProps,
  type CreateTaskReference,
} from '@github-ui/agent-sessions/components/new-task-chat-input/NewTaskChatInput'
import type {DropdownRepository} from '@github-ui/agent-sessions/hooks/useCopilotRepoFetching'
import type {IntentDetectionMode} from '@github-ui/agent-sessions/types/intent-detection-mode'
import {CopilotChatProvider} from '@github-ui/copilot-chat/CopilotChatContext'
import {SkeletonBox} from '@primer/react'
import {useCurrentRepository, type Repository} from '@github-ui/current-repository'
// eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-imports
import {useCopilotChatPayloadQuery} from '@github-ui/global-copilot-menu/hooks/useCopilotChatPayloadQuery'
import type {CreateAPITaskParams} from '@github-ui/agent-sessions/types/api-task'
import type {SelectedAgent} from '@github-ui/agent-sessions/types/session'
import {IntentDetectionProvider} from '@github-ui/agent-sessions/contexts/IntentDetectionContext'

export type NewAgentTaskFormProps = {
  createTask: (task: CreateAPITaskParams) => void
  currentRepo?: Repository
  currentReference?: CreateTaskReference
  onCreate?: () => void
  showHeader?: boolean
  hideRepoSelector?: boolean
  eventType: string
  isInPortal?: NewTaskChatInputProps['isInPortal']
  textAreaRef?: NewTaskChatInputProps['textAreaRef']
  className?: string
  initialText?: string
  initialBranch?: string
  /** Maximum height for the textarea input in pixels */
  maxTextareaHeight?: number
  /** Whether or not to use task mode - when false this should default to using the 'fix' agent and creating a pull request */
  taskModeEnabled?: boolean
  /** Dispatcher for local-device submissions, mirroring `createTask` for the
   * cloud path. The parent owns the underlying `useCreateLocalDeviceSession`
   * mutation hook (so it can wire it to its own optimistic-list config) and
   * passes the dispatcher down. When omitted, the local environment
   * picker option is non-submittable. */
  onLocalDeviceSubmit?: NewTaskChatInputProps['onLocalDeviceSubmit']
}

export function NewAgentTaskForm({
  createTask,
  currentRepo,
  currentReference,
  onCreate,
  showHeader = true,
  hideRepoSelector,
  eventType,
  isInPortal,
  textAreaRef,
  className,
  initialText,
  initialBranch,
  maxTextareaHeight,
  taskModeEnabled,
  onLocalDeviceSubmit,
}: NewAgentTaskFormProps) {
  const copilotChatPayload = useCopilotChatPayloadQuery()
  const agentQuotaSnapshot = copilotChatPayload?.agentQuotaSnapshot
  const currentRepoFromContext = useCurrentRepository()
  const initialRepo = currentRepo || currentRepoFromContext

  const handleUserSubmit = async (
    content: string,
    selectedRepo: DropdownRepository | null,
    selectedBranch: string,
    selectedAgent: SelectedAgent,
    _selectedMode?: IntentDetectionMode,
    modelId?: string,
    createPullRequest?: boolean,
  ) => {
    const trimmedContent = content.trim()
    if (trimmedContent === '') return
    if (!selectedRepo) return
    const newTask: CreateAPITaskParams = {
      nwo: selectedRepo.nameWithOwner,
      problemStatement: trimmedContent,
      baseRef: selectedBranch || selectedRepo.defaultBranch,
      eventType,
      eventContent: trimmedContent,
      intentDetectionMode: 'task', // For now we explicitly don't use intent detection on this path
      createPullRequest: createPullRequest ?? (taskModeEnabled === false ? true : undefined),
      model: modelId,
      customAgent: selectedAgent?.subAgentSlug,
      agentId: selectedAgent?.topLevelAgentId,
    }
    createTask(newTask)

    onCreate?.()
  }

  return (
    <>
      {showHeader && (
        <div className={styles.header}>
          <h2 className="f5">New agent session</h2>
        </div>
      )}
      <div className={clsx(styles.content, !showHeader && 'tmp-pt-3', className)}>
        {copilotChatPayload ? (
          <CopilotChatProvider
            workerPath={copilotChatPayload.findFileWorkerPath}
            threadId={null}
            refs={copilotChatPayload.refs || []}
            mode="immersive"
            ssoOrganizations={copilotChatPayload.ssoOrganizations || []}
            copilotChatPayload={copilotChatPayload}
          >
            <IntentDetectionProvider>
              <NewTaskChatInput
                handleUserSubmit={handleUserSubmit}
                initialRepo={initialRepo}
                initialReference={currentReference}
                hideRepoSelector={hideRepoSelector}
                isInPortal={isInPortal}
                textAreaRef={textAreaRef}
                size="small"
                eventType={eventType}
                initialText={initialText}
                initialBranch={initialBranch}
                maxTextareaHeight={maxTextareaHeight}
                onLocalDeviceSubmit={onLocalDeviceSubmit}
                agentQuotaSnapshot={agentQuotaSnapshot}
              />
            </IntentDetectionProvider>
          </CopilotChatProvider>
        ) : (
          <SkeletonBox className={styles.skeleton} />
        )}
      </div>
    </>
  )
}
